import { makeAutoObservable } from 'mobx';
import { Utils } from '../utils/Utils';
import { DEFAULT_PAGE_SCALE } from '../constant';

let closetIndex = 1;
export class Closet {
    _id = null;
    _coordinates = [];
    _pageNo = null;
    _scaleOnPdf = 1;
    _unitId = null;
    _name = null;

    constructor(id, pageNo, scale) {
        makeAutoObservable(this);
        this._id = id;
        this._pageNo = pageNo;
        this._scaleOnPdf = scale || DEFAULT_PAGE_SCALE;
        this._name = `Closet-${closetIndex++}`;
    }

    get id() {
        return this._id;
    }

    get unitId() {
        return this._unitId;
    }

    get coordinates() {
        return this._coordinates;
    }

    get pageNo() {
        return this._pageNo;
    }

    get scaleOnPdf() {
        return this._scaleOnPdf;
    }
    get name() {
        return this._name;
    }

    setName(name) {
        this._name = name;
    }

    addCoordinate(coordinate) {
        this._coordinates.push(...coordinate);
    }

    setAllCoordinates(coordinates) {
        this._coordinates = coordinates;
    }
    isClosetInTol(pointToCheck, tol) {
        const polygon = this.coordinates;

        function getDistance(p1, p2) {
            return Math.sqrt((p1.x - p2.x) ** 2 + (p1.y - p2.y) ** 2);
        }

        function isPointOnSegment(p, a, b, tol) {
            const d1 = getDistance(a, p);
            const d2 = getDistance(b, p);
            const d_ab = getDistance(a, b);

            return Math.abs(d1 + d2 - d_ab) <= tol;
        }

        for (let vertex of polygon) {
            if (getDistance(pointToCheck, vertex) <= tol) {
                return true;
            }
        }

        for (let i = 0; i < polygon.length; i++) {
            const a = polygon[i];
            const b = polygon[(i + 1) % polygon.length];

            if (isPointOnSegment(pointToCheck, a, b, tol)) {
                return true;
            }
        }

        return false;
    }

    isPointInCenter(pointToCheck, tol) {
        const polygon = this.coordinates;
        function getDistance(p1, p2) {
            return Math.sqrt((p1.x - p2.x) ** 2 + (p1.y - p2.y) ** 2);
        }
        let meanX = 0;
        let meanY = 0;
        for (let i = 0; i < polygon.length; i++) {
            meanX += polygon[i].x;
            meanY += polygon[i].y;
        }
        meanX /= polygon.length;
        meanY /= polygon.length;
        return getDistance(pointToCheck, { x: meanX, y: meanY }) <= tol;
    }

    findClosetByCoordinate(coordinate, tol) {
        for (let i = 0; i < this.coordinates.length; i++) {
            const point = this.coordinates[i];
            const dist = Math.sqrt(
                (point.x - coordinate.x) ** 2 + (point.y - coordinate.y) ** 2,
            );
            if (dist <= tol) {
                return point;
            }
        }
        return null;
    }

    closestCoordinate = (coordinate) => {
        let minDist = Infinity;
        let closestPoint = null;
        for (let i = 0; i < this.coordinates.length; i++) {
            const point = this.coordinates[i];
            const dist = Math.sqrt(
                (point.x - coordinate.x) ** 2 + (point.y - coordinate.y) ** 2,
            );
            if (dist < minDist) {
                minDist = dist;
                closestPoint = point;
            }
        }
        return closestPoint;
    };

    getClosetEdgeByCoordinate = (coordinate, tol) => {
        for (let i = 0; i < this.coordinates.length; i++) {
            const p1 = this.coordinates[i];
            const p2 = this.coordinates[(i + 1) % this.coordinates.length];
            const dist1 = Math.sqrt(
                (p1.x - coordinate.x) ** 2 + (p1.y - coordinate.y) ** 2,
            );
            const dist2 = Math.sqrt(
                (p2.x - coordinate.x) ** 2 + (p2.y - coordinate.y) ** 2,
            );
            const dist3 = Math.sqrt((p2.x - p1.x) ** 2 + (p2.y - p1.y) ** 2);
            if (dist1 + dist2 - dist3 < tol) {
                return [p1, p2];
            }
        }
        return null;
    };

    addNewPointInLine = (inIndex) => {
        const p1 = this.coordinates[inIndex];
        const p2 = this.coordinates[(inIndex + 1) % this.coordinates.length];
        const newPoint = {
            x: (p1.x + p2.x) / 2,
            y: (p1.y + p2.y) / 2,
        };
        this.coordinates.splice(inIndex + 1, 0, newPoint);
    };

    deletePointByCoordinate = (coordinate) => {
        const index = this.coordinates.findIndex(
            (point) => point.x === coordinate.x && point.y === coordinate.y,
        );
        if (index >= 0) {
            this.coordinates.splice(index, 1);
        }
    };

    checkPointInMiddleOfAnyLine = (coordinate, tol = 10) => {
        for (let i = 0; i < this.coordinates.length; i++) {
            const p1 = this.coordinates[i];
            const p2 = this.coordinates[(i + 1) % this.coordinates.length];
            const middleX = (p1.x + p2.x) / 2;
            const middleY = (p1.y + p2.y) / 2;
            const distToMiddle = Math.sqrt(
                (coordinate.x - middleX) ** 2 + (coordinate.y - middleY) ** 2,
            );
            if (distToMiddle < tol) {
                return i;
            }
        }
        return -1;
    };

    setUnitId(unitId) {
        this._unitId = unitId;
    }

    isClosetIntersecting(closetCoords, index = 0) {
        for (let i = 0; i < this.coordinates.length; i++) {
            const p1 = this.coordinates[i];
            const p2 = this.coordinates[(i + 1) % this.coordinates.length];
            for (let j = 0; j < closetCoords.length - index; j++) {
                const p3 = closetCoords[j];
                const p4 = closetCoords[(j + 1) % closetCoords.length];
                if (Utils.isLineIntersect(p1, p2, p3, p4)) {
                    return true;
                }
            }
        }

        for (let i = 0; i < closetCoords.length; i++) {
            if (Utils.pointInPolygon(closetCoords[i], this.coordinates)) {
                return true;
            }
        }

        if (index == 0) {
            for (let i = 0; i < this.coordinates.length; i++) {
                if (Utils.pointInPolygon(this.coordinates[i], closetCoords)) {
                    return true;
                }
            }
        }
        return false;
    }
}
